"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Parser = exports.astToString = exports.astToTokens = exports.ParserError = exports.isASTNode = exports.isASTree = exports.AbstractSyntaxType = void 0;
const token_1 = require("./token");
var AbstractSyntaxType;
(function (AbstractSyntaxType) {
    AbstractSyntaxType["ROOT"] = "root";
    AbstractSyntaxType["EXPR"] = "expr";
    AbstractSyntaxType["PATH"] = "path";
    AbstractSyntaxType["FUNC"] = "func";
    AbstractSyntaxType["ERR"] = "err";
    AbstractSyntaxType["OPERATOR"] = "operator";
    AbstractSyntaxType["CONDITIONAL"] = "conditional";
    AbstractSyntaxType["STATEMENT"] = "statement";
})(AbstractSyntaxType = exports.AbstractSyntaxType || (exports.AbstractSyntaxType = {}));
function isASTree(arg) {
    return isASTNode(arg) || token_1.isToken(arg);
}
exports.isASTree = isASTree;
function isASTNode(arg) {
    return arg.children !== undefined;
}
exports.isASTNode = isASTNode;
function newNode(type) {
    return { children: [], type };
}
class ParserError extends Error {
    constructor(message) {
        super(message);
        this.message = message;
        this.name = 'LexerError';
        this.stack = new Error().stack;
    }
}
exports.ParserError = ParserError;
function ast(tokens) {
    try {
        // we .slice() here to avoid destroying the array
        return { node: new Parser(tokens.slice()).parse() };
    }
    catch (error) {
        return {
            node: newNode(AbstractSyntaxType.ERR),
            error
        };
    }
}
exports.default = ast;
function astToTokens(node) {
    const tokens = traverseAstForTokens(node);
    // AST doesn't record the EOS, so we add it back
    tokens.push(token_1.t.EOS());
    return tokens;
}
exports.astToTokens = astToTokens;
function formatToken(token) {
    switch (token.type) {
        case token_1.TokenType.EOS:
            return ''; // don't show EOS tokens
        case token_1.TokenType.Conditional:
            // 'or' and 'and' needs spaces to avoid converting things to:
            // `ortype = "hello"` instead of `or type = "hello"`
            return ' ' + token.value + ' ';
        case token_1.TokenType.Operator:
            // Operators are formatted with spaces for easier reading
            // prefer `type = "hello"` to `type="hello"`
            return ' ' + token.value + ' ';
        default:
            return token.value;
    }
}
function astToString(node) {
    return astToTokens(node)
        .map(formatToken)
        .join('');
}
exports.astToString = astToString;
function interleave(array, thing) {
    return [].concat(...array.map(obj => [obj, thing])).slice(0, -1);
}
// AST strips away parentheses and commas,
// add them back for token representation.
function traverseFunctionNodeForTokens(func) {
    const [functionIdentifier, ...operands] = func.children;
    let operandTokens = operands.map(operand => {
        if ('children' in operand) {
            return traverseAstForTokens(operand);
        }
        return operand;
    });
    operandTokens = interleave(operandTokens, token_1.t.Comma());
    const flattenedOperandTokens = [].concat(...operandTokens);
    return [
        functionIdentifier,
        token_1.t.ParenLeft(),
        ...flattenedOperandTokens,
        token_1.t.ParenRight()
    ];
}
function traverseAstForTokens(tree) {
    let tokens = [];
    for (const child of tree.children) {
        if (isASTNode(child)) {
            if (child.type === 'func') {
                const moreTokens = traverseFunctionNodeForTokens(child);
                tokens = tokens.concat(moreTokens);
            }
            else {
                const moreTokens = traverseAstForTokens(child);
                tokens = tokens.concat(moreTokens);
            }
        }
        else if (token_1.isToken(child)) {
            tokens.push(child);
        }
    }
    return tokens;
}
class Parser {
    constructor(tokens) {
        this.queue = tokens;
    }
    parse() {
        const node = newNode(AbstractSyntaxType.ROOT);
        node.children.push(this.statement());
        while (this.peek().type === token_1.TokenType.Conditional || this.peek().type === token_1.TokenType.EOS) {
            if (this.peek().type === token_1.TokenType.EOS) {
                return node;
            }
            node.children.push(this.conditional());
            node.children.push(this.statement());
        }
        // Future versions could get better error handling here
        // by passing in the AST up to this point, converting it
        // back to tokens, unlexing those tokens, and then using
        // where it stopped to signal to the user where the bug is
        throw new ParserError(`Unexpected token of type '${this.peek().type}' and value '${this.peek().value}'.`);
    }
    statement() {
        const node = newNode(AbstractSyntaxType.STATEMENT);
        const expr = this.expr();
        node.children.push(expr);
        if (this.peek().type === token_1.TokenType.EOS) {
            return node;
        }
        if (this.peek().type === token_1.TokenType.Operator) {
            node.children.push(this.operator());
            node.children.push(this.expr());
            return node;
        }
        const [firstChild] = expr.children;
        const exprIsFunc = firstChild && firstChild.type === AbstractSyntaxType.FUNC;
        if (exprIsFunc && this.peek().type === token_1.TokenType.Conditional) {
            return node;
        }
        throw new ParserError(`Unexpected token in statement: '${this.peek()}'`);
    }
    // Wrapped in a leaf so we get a clear ordering
    // when parsing
    conditional() {
        const node = newNode(AbstractSyntaxType.CONDITIONAL);
        node.children.push(this.next());
        return node;
    }
    // We wrap the operator in a node instead
    // of a leaf so we get clear ordering
    operator() {
        const node = newNode(AbstractSyntaxType.OPERATOR);
        node.children.push(this.next());
        return node;
    }
    expr() {
        const node = newNode(AbstractSyntaxType.EXPR);
        const upcoming = this.peek();
        // Strings
        if (upcoming.type === token_1.TokenType.String) {
            node.children.push(this.next());
            return node;
        }
        // Numbers
        if (upcoming.type === token_1.TokenType.Number) {
            node.children.push(this.next());
            return node;
        }
        // Null
        if (upcoming.type === token_1.TokenType.Null) {
            node.children.push(this.next());
            return node;
        }
        // Paths or functions
        if (upcoming.type === token_1.TokenType.Ident) {
            node.children.push(this.pathOrFunc(this.next()));
            return node;
        }
        // Lists
        if (upcoming.type === token_1.TokenType.BrackLeft) {
            node.children.push(this.list());
            return node;
        }
        // Unrecognized tokens
        throw new ParserError(`Unsupported or unrecognized token: ${upcoming}`);
    }
    // things like `message.event` or `contains(...)`
    pathOrFunc(previous) {
        const nextToken = this.peek();
        if (nextToken.type === token_1.TokenType.ParenLeft) {
            return this.func(previous);
        }
        return this.path(previous);
    }
    path(previous) {
        const node = newNode(AbstractSyntaxType.PATH);
        node.children.push(previous);
        while (this.peek().type === token_1.TokenType.Ident || this.peek().type === token_1.TokenType.Dot) {
            node.children.push(this.next());
        }
        return node;
    }
    func(previous) {
        const supportedFuncs = ['contains', 'match'];
        if (!(previous.type === 'ident' && supportedFuncs.indexOf(previous.value) !== -1)) {
            throw new Error(`Function not supported: ${previous.value}`);
        }
        const node = newNode(AbstractSyntaxType.FUNC);
        node.children.push(previous);
        const leftParens = this.next();
        if (leftParens.type !== token_1.TokenType.ParenLeft) {
            throw new Error(`Unexpected token in function: "${leftParens.value}"`);
        }
        // Left operand can be a string or path.
        const leftOperand = newNode(AbstractSyntaxType.EXPR);
        const upcoming = this.peek();
        if (upcoming.type === token_1.TokenType.String) {
            leftOperand.children.push(this.next());
        }
        else if (upcoming.type === token_1.TokenType.Ident) {
            leftOperand.children.push(this.path(this.next()));
        }
        else {
            throw new Error(`Unexpected token in function: "${upcoming.value}"`);
        }
        node.children.push(leftOperand);
        const comma = this.next();
        if (comma.type !== token_1.TokenType.Comma) {
            throw new Error(`Unexpected token in function: "${comma.value}"`);
        }
        const substring = this.next();
        if (substring.type !== token_1.TokenType.String) {
            throw new Error(`Unexpected token in function: "${substring.value}"`);
        }
        const substringNode = newNode(AbstractSyntaxType.EXPR);
        substringNode.children.push(substring);
        node.children.push(substringNode);
        const rightParens = this.next();
        if (rightParens.type !== token_1.TokenType.ParenRight) {
            throw new Error(`Unexpected token in function: ${rightParens.value}`);
        }
        return node;
    }
    list() {
        throw new ParserError(` list Not yet supported`);
    }
    peek() {
        if (this.queue.length > 0) {
            return this.queue[0];
        }
        return token_1.t.EOS();
    }
    next() {
        const upcoming = this.peek();
        if (upcoming.type !== token_1.TokenType.EOS) {
            return this.queue.shift();
        }
        return token_1.t.EOS();
    }
}
exports.Parser = Parser;
//# sourceMappingURL=ast.js.map